import React from "react";

/**
 * SplitPanel — the article-page layout, shared by every "one subject, read
 * top to bottom" page (recipe, camera hub, reference, type gallery, static).
 *
 * It's the gallery modal's look promoted to a full page:
 *   - Desktop: a fixed image panel on the left that does NOT scroll, and a
 *     content column on the right that does. The image is pinned with
 *     position: sticky so it stays in view while the article scrolls past.
 *   - Mobile: the image sits on top and scrolls away with the content,
 *     exactly like the reference sample's modal on small screens.
 *
 * This is layout only — the page still renders real, crawlable content in
 * the DOM (SEO unaffected), and each page keeps its own <h1>, meta and
 * JSON-LD. The image panel is decorative (aria-hidden); it never holds the
 * heading or any indexable copy.
 *
 * Props:
 *   gradient   {from,to}     — lighting/neutral gradient behind the panel
 *   image      string        — optional photo URL (blended over the gradient)
 *   imageText  {eyebrow,title,sub} — overlaid label on the image panel
 *   children                 — the scrolling article content
 *   contentClassName         — extra class on the content column
 */
export default function SplitPanel({
  gradient = { from: "#2A2C32", to: "#17181C" },
  image,
  imageAlt,
  imageText,
  children,
  contentClassName = "",
}) {
  return (
    <div className="split">
      <aside
        className="split-image"
        style={{ "--grad-from": gradient.from, "--grad-to": gradient.to }}
        aria-hidden="true"
      >
        <div className="split-image-inner">
          {image && (
            <img
              src={image}
              alt={imageAlt || ""}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="split-image-tint" />
          {imageText && (
            <div className="split-image-overlay">
              {imageText.eyebrow && <span className="split-image-eyebrow">{imageText.eyebrow}</span>}
              {imageText.title && <p className="split-image-title">{imageText.title}</p>}
              {imageText.sub && <span className="split-image-sub">{imageText.sub}</span>}
            </div>
          )}
        </div>
      </aside>

      <div className={`split-content ${contentClassName}`.trim()}>{children}</div>
    </div>
  );
}
